import React, { useEffect, useState } from "react";
import { Nav, Offcanvas, Tab, Tabs } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { v4 as uuidv4 } from "uuid";
import { useSelector } from "react-redux";
import {
  faCheck,
  faMinusCircle,
  faPlus,
  faPlusCircle,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import JsonCollapse from "./JsonCollapse";
import Input from "../nodes/Logic/Inputs/Input";
import timezones from "../nodes/Logic/timezones";
import {
  selectCounterCategories,
  selectDataCategories,
  selectEventHandlers,
  selectInputArguments,
  selectParticipantCategories,
} from "../../store/slices/projectSlice";
import { selectimportedContactFields } from "../../store/slices/broadcastSlice";
import { useGetContactFieldsQuery } from "../../store/slices/contactsSlice";
import { toast } from "react-toastify";
import { pageProps } from "../../helpers/pageProps";
import HelpButton from "../../HelpContent/HelpButton";
import {
  useGetApiDataCollectionQuery,
  useGetApiDataCollectionsQuery,
  useGetDataEntryStructureQuery,
  useGetSystemDataStructuresQuery,
} from "../../store/slices/settingsSlice";
import { proxyAPI } from "../../proxyAPI";
import CompoundInput from "../nodes/Logic/Inputs/CompoundInput";
import StyledTabContainer from "../../Common/StyledTabContainer";

const tokenCategoryOptions = {
  contact_field: {
    Value: "", // selectable
    DefaultValue: "No value provided", // optional
    Label: "", // optional
    _label: "Contact Field",
  },
  input_argument: {
    Value: "", // selectable
    DefaultValue: "[VALUE]", // optional
    Label: "", // optional
    Format: "raw_value", // "raw_value" "parse_as_date" // if variable format is non-comms
    DateFormatFrom: "YYYY-MM-DD hh:mm",
    DateFormatTo: "YYYY-MM-DD hh:mm",
    TimeZoneFrom: "",
    TimeZoneTo: "variableDefault",
    Limit: 100,
    Offset: 0,
    Conditions: [],
    UseMainThread: true,
    _label: "Variable",
  },
  input_argument_count: {
    Value: "", // selectable
    DefaultValue: "[VALUE]", // optional
    Label: "", // optional
    UseMainThread: true,
    _label: "Variable Count",
  },
  survey: {
    Label: "",
    DefaultValue: "",
    _value: "Survey Link",
  },
  workflow_name: {
    Label: "",
    DefaultValue: "[VALUE]",
    _value: "Current Workflow Label",
  },
  guid: {
    Value: "",
    Label: "",
    DefaultValue: "[VALUE]",
    _value: "Process GUID",
  },
  component_name: {
    Value: "",
    Label: "",
    DefaultValue: "[VALUE]",
    _value: "Component Name",
  },
  current_time: {
    Value: "",
    Label: "",
    DefaultValue: "[VALUE]",
    DateFormatTo: "YYYY-MM-DD HH:mm",
    TimeZoneToValue: "",
    _value: "Current Time",
  },
  customer_name: {
    Value: "",
    Label: "",
    DefaultValue: "[VALUE]",
    _value: "Customer Name",
  },
  monitor: {
    Label: "",
    DefaultValue: "",
  },
  api: {
    Label: "",
    DefaultValue: "",
  },
  ping_status: {
    Label: "",
    DefaultValue: "",
  },
  weather: {
    Label: "",
    DefaultValue: "",
  },
  assets: {
    Label: "",
    DefaultValue: "",
    _value: "Assets table",
  },
  events: {
    Label: "",
    DefaultValue: "",
    _value: "Events table",
  },
};

const tokensWithoutValue = [
  "survey",
  "workflow_name",
  "guid",
  "component_name",
  "current_time",
  "customer_name",
  "assets",
  "events",
];

const simpleWorkflowRuntimeTags = [
  "workflow_name",
  "guid",
  "component_name",
  "current_time",
  "customer_name",
];

const contextTokenOptions = [
  { tag: "customer_name", label: "Customer name" },
  { tag: "component_name", label: "Component name" },
  { tag: "workflow_name", label: "Workflow name" },
  { tag: "guid", label: "Process GUID" },
  { tag: "current_time", label: "Current time" },
  { tag: "survey", label: "Survey" },
];

const tokenTagsWithTagPrinter = [
  "contact_field",
  "input_argument",
  "input_argument_count",
  "workflow_name",
  "guid",
  "component_name",
  "current_time",
  "customer_name",
];

function getDefaultCurrentTimeZone(currentTimeDefaultTimeZone) {
  return currentTimeDefaultTimeZone ?? pageProps.defaultTZ ?? "";
}

function withTagPrinter(token) {
  if (!tokenTagsWithTagPrinter.includes(token?.Tag)) {
    return token;
  }

  return {
    ...token,
    TagPrinter: {
      ...(token.TagPrinter || {}),
      Label: token.Label ?? token.TagPrinter?.Label ?? "",
      DefaultValue: token.DefaultValue ?? token.TagPrinter?.DefaultValue ?? "",
      ...(token.DateFormatTo || token.TagPrinter?.DateFormatTo
        ? {
            DateFormatTo: token.DateFormatTo ?? token.TagPrinter?.DateFormatTo,
          }
        : {}),
      ...(token.Format || token.TagPrinter?.Format
        ? { Format: token.Format ?? token.TagPrinter?.Format }
        : {}),
      ...(token.TimeZoneTo || token.TagPrinter?.TimeZoneTo
        ? { TimeZoneTo: token.TimeZoneTo ?? token.TagPrinter?.TimeZoneTo }
        : {}),
      ...(token.Tag === "current_time" ||
      token.TimeZoneToValue ||
      token.TagPrinter?.TimeZoneToValue
        ? {
            TimeZoneToValue:
              token.TimeZoneToValue ?? token.TagPrinter?.TimeZoneToValue ?? "",
          }
        : {}),
      ...(token.TimeZoneUseMainThread || token.TagPrinter?.TimeZoneUseMainThread
        ? {
            TimeZoneUseMainThread:
              token.TimeZoneUseMainThread ??
              token.TagPrinter?.TimeZoneUseMainThread,
          }
        : {}),
      ...(token.Conditions || token.TagPrinter?.Conditions
        ? { Conditions: token.Conditions ?? token.TagPrinter?.Conditions }
        : {}),
    },
  };
}

function tokenForEdit(token) {
  if (!token) {
    return token;
  }

  return {
    ...token,
    Label: token.Label ?? token.TagPrinter?.Label ?? "",
    DefaultValue: token.DefaultValue ?? token.TagPrinter?.DefaultValue ?? "",
    Format: token.Format ?? token.TagPrinter?.Format,
    DateFormatTo: token.DateFormatTo ?? token.TagPrinter?.DateFormatTo,
    TimeZoneTo: token.TimeZoneTo ?? token.TagPrinter?.TimeZoneTo,
    TimeZoneToValue: token.TimeZoneToValue ?? token.TagPrinter?.TimeZoneToValue,
    TimeZoneUseMainThread:
      token.TimeZoneUseMainThread ?? token.TagPrinter?.TimeZoneUseMainThread,
    Conditions: token.Conditions ?? token.TagPrinter?.Conditions,
  };
}

function TokenCommonFields({
  editedToken,
  setEditedToken,
  selectedVariable,
  errors,
  inputArguments,
  dataCategories,
  fieldName,
}) {
  return (
    <>
      {(editedToken?.PrintAs !== "table" || fieldName) &&
        ![
          "survey",
          "input_argument_count",
          "contact_field",
          "monitor",
          "api",
          "ping_status",
          "assets",
          "events",
          "weather",
          ...simpleWorkflowRuntimeTags,
        ].includes(editedToken?.Tag) && (
          <div className="mb-3">
            <label htmlFor="inputTokenSeparator" className="form-label">
              Separator {fieldName && `for values in ${fieldName}`}{" "}
              <HelpButton helpKey="message-token-separator" />
            </label>
            <input
              type="text"
              placeholder="Separator"
              className="form-control"
              id="inputTokenSeparator"
              value={editedToken?.Separator ?? ""}
              onChange={(e) =>
                setEditedToken((prev) => ({
                  ...prev,
                  Separator: e.target.value,
                }))
              }
            />
          </div>
        )}

      {/* Label */}
      <div className="mb-3">
        <label htmlFor="inputTokenLabel" className="form-label">
          Label {fieldName && `for ${fieldName}`}{" "}
          <HelpButton helpKey="message-token-label" />
        </label>
        <input
          type="text"
          placeholder="Token label"
          className="form-control"
          id="inputTokenLabel"
          value={editedToken?.Label ?? ""}
          onChange={(e) =>
            setEditedToken((prev) => ({
              ...prev,
              Label: e.target.value,
            }))
          }
        />
      </div>

      {[
        "contact_field",
        "monitor",
        "api",
        "ping_status",
        "assets",
        "events",
        "weather",
      ].includes(editedToken?.Tag) && (
        <div className="mb-3">
          <label className="form-label">Default value</label>
          <textarea
            className="form-control"
            value={editedToken?.DefaultValue}
            onChange={(e) =>
              setEditedToken((prev) => ({
                ...prev,
                DefaultValue: e.target.value,
              }))
            }
          />
        </div>
      )}

      {
        // Default value for exists
        editedToken?.Tag === "input_argument_exists" && (
          <>
            <div className="mb-3">
              <label htmlFor="inputTokenTrueValue" className="form-label">
                Default value for exists
              </label>
              <input
                type="text"
                placeholder="Default true value"
                className="form-control"
                id="inputTokenTrueValue"
                maxLength={255}
                value={editedToken?.TrueValue ?? ""}
                onChange={(e) =>
                  setEditedToken((prev) => ({
                    ...prev,
                    TrueValue: e.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-3">
              <label htmlFor="inputTokenFalseValue" className="form-label">
                Default value for does not exist
              </label>
              <input
                type="text"
                placeholder="Default false value"
                className="form-control"
                id="inputTokenFalseValue"
                maxLength={255}
                value={editedToken?.FalseValue ?? ""}
                onChange={(e) =>
                  setEditedToken((prev) => ({
                    ...prev,
                    FalseValue: e.target.value,
                  }))
                }
              />
            </div>
          </>
        )
      }

      {(editedToken?.Format === "parse_as_date" ||
        (editedToken?.Tag === "input_argument" &&
          ["date", "dateTime", "time"].includes(selectedVariable?.Format))) && (
        <div className="mb-3">
          <label className="form-label">Parse to</label>
          <div className="">
            <div className="mb-3">
              <label htmlFor="inputTokenDateFormatTo" className="form-label">
                Date format (YYYY-MM-DD or HH:mm)
              </label>
              <input
                type="text"
                placeholder="Date format"
                className="form-control"
                id="inputTokenDateFormatTo"
                value={editedToken?.DateFormatTo ?? ""}
                onChange={(e) =>
                  setEditedToken((prev) => ({
                    ...prev,
                    DateFormatTo: e.target.value,
                  }))
                }
              />
            </div>
            {errors.DateFormatTo && (
              <div className="alert alert-danger">{errors.DateFormatTo}</div>
            )}
            <div>
              <div className="mb-3">
                <label htmlFor="inputTokenDateFormatTo" className="form-label">
                  Time zone source
                </label>
                <Input
                  maxLength={255}
                  inputType="select"
                  isValidNewOption={() => false}
                  options={[
                    {
                      value: "variableDefault",
                      label: `Variable default (${
                        selectedVariable?.DefaultTimeZone ||
                        `Project default time zone - ${pageProps.defaultTZ}`
                      })`,
                    },
                    {
                      value: "fixed",
                      label: "Fixed time zone",
                    },
                    {
                      value: "inputArgument",
                      label: "From variable",
                    },
                  ]}
                  value={[editedToken?.TimeZoneTo ?? ""]}
                  onChange={(values) =>
                    setEditedToken((prev) => ({
                      ...prev,
                      TimeZoneTo: values[0],
                      TimeZoneToValue:
                        values[0] === "inputArgument"
                          ? inputArguments.find((arg) =>
                              ["short-text"].includes(arg.Format)
                            )?.UUID
                          : "",
                    }))
                  }
                />
              </div>

              {[editedToken?.TimeZoneTo ?? ""].includes("fixed") && (
                <div className="mb-3">
                  <label
                    htmlFor="inputTokenDateFormatTo"
                    className="form-label"
                  >
                    Select time zone
                  </label>
                  <Input
                    maxLength={255}
                    inputType="select"
                    isValidNewOption={() => false}
                    value={[editedToken?.TimeZoneToValue ?? ""]}
                    onChange={(values) =>
                      setEditedToken((prev) => ({
                        ...prev,
                        TimeZoneToValue: values[0],
                      }))
                    }
                    options={timezones.map((tz) => ({
                      value: tz.tzCode,
                      label: tz.label,
                    }))}
                  />
                </div>
              )}
              {[editedToken?.TimeZoneTo ?? ""].includes("inputArgument") && (
                <div className="mb-3">
                  <label
                    htmlFor="inputTokenDateFormatTo"
                    className="form-label"
                  >
                    Select variable
                  </label>
                  <Input
                    maxLength={255}
                    inputType="select"
                    isValidNewOption={() => false}
                    value={[editedToken?.TimeZoneToValue ?? ""]}
                    onChange={(values) =>
                      setEditedToken((prev) => ({
                        ...prev,
                        TimeZoneToValue: values[0],
                      }))
                    }
                    options={inputArguments
                      .filter((arg) => ["short-text"].includes(arg.Format))
                      .map((variable) => ({
                        value: variable.UUID,
                        label: variable.Label,
                      }))}
                  />
                  <div className="my-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="tokenTZUseDefaultRuntimeBranch"
                      checked={editedToken?.TimeZoneUseMainThread ?? false}
                      onChange={() =>
                        setEditedToken((prev) => ({
                          ...prev,
                          TimeZoneUseMainThread: !prev.TimeZoneUseMainThread,
                        }))
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="tokenTZUseDefaultRuntimeBranch"
                    >
                      Use main thread for time zone?
                    </label>
                  </div>
                </div>
              )}
              {[editedToken?.TimeZoneTo ?? ""].includes(
                "storageData:category"
              ) && (
                <>
                  <div className="mb-3">
                    <label
                      htmlFor="inputTokenDateFormatTo"
                      className="form-label"
                    >
                      Data category
                    </label>
                    <Input
                      maxLength={255}
                      inputType="select"
                      options={dataCategories.map((cat) => ({
                        value: cat,
                        label: cat,
                      }))}
                      value={[editedToken?.TimeZoneToValue ?? ""]}
                      onChange={(values) =>
                        setEditedToken((prev) => ({
                          ...prev,
                          TimeZoneToValue: values[0],
                        }))
                      }
                      isValidNewOption={(v) => v}
                      onCreateOption={(option) => {
                        setEditedToken((prev) => ({
                          ...prev,
                          TimeZoneToValue: option,
                        }));
                      }}
                    />
                  </div>
                  <div className="mb-3 form-check">
                    <label className="form-label">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={editedToken?.TimeZoneToThread === "MainThread"}
                        onChange={() =>
                          setEditedToken((prev) => ({
                            ...prev,
                            TimeZoneToThread:
                              editedToken?.TimeZoneToThread === "MainThread"
                                ? "CurrentThread"
                                : "MainThread",
                          }))
                        }
                      />
                      Use main runtime thread?
                    </label>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {editedToken?.Tag === "current_time" && (
        <div className="mb-3">
          <div className="mb-3">
            <label
              htmlFor="inputTokenCurrentTimeDateFormat"
              className="form-label"
            >
              Date format (YYYY-MM-DD or HH:mm)
            </label>
            <input
              type="text"
              placeholder="Date format"
              className="form-control"
              id="inputTokenCurrentTimeDateFormat"
              value={editedToken?.DateFormatTo ?? ""}
              onChange={(e) =>
                setEditedToken((prev) => ({
                  ...prev,
                  DateFormatTo: e.target.value,
                }))
              }
            />
          </div>
          <div className="mb-3">
            <label
              htmlFor="inputTokenCurrentTimeTimeZone"
              className="form-label"
            >
              Time zone
            </label>
            <Input
              maxLength={255}
              inputType="select"
              isValidNewOption={() => false}
              value={[editedToken?.TimeZoneToValue ?? ""]}
              onChange={(values) =>
                setEditedToken((prev) => ({
                  ...prev,
                  TimeZoneToValue: values[0],
                }))
              }
              options={timezones.map((tz) => ({
                value: tz.tzCode,
                label: tz.label,
              }))}
            />
          </div>
        </div>
      )}

      {
        // Conditions
        (editedToken?.Tag === "input_argument" ||
          editedToken?.Tag === "input_argument_count" ||
          simpleWorkflowRuntimeTags.includes(editedToken?.Tag) ||
          fieldName) && (
          <>
            <div className="mb-3">
              <label className="form-label">Default value</label>
              <textarea
                className="form-control"
                value={editedToken?.DefaultValue}
                onChange={(e) =>
                  setEditedToken((prev) => ({
                    ...prev,
                    DefaultValue: e.target.value,
                  }))
                }
              />
            </div>
            <div className="mb-2">
              <button
                type="button"
                className="btn btn-outline-primary btn-sm border-0 float-end"
                onClick={() =>
                  setEditedToken((prev) => ({
                    ...prev,
                    Conditions: [
                      ...(prev.Conditions || []),
                      {
                        Condition: "one-of",
                        ...(editedToken?.Tag === "counter"
                          ? { ConditionNumber: 0 }
                          : { ConditionText: [] }),
                        Text: "[VALUE]",
                        Order: prev.Conditions?.length ?? 0,
                      },
                    ],
                  }))
                }
              >
                <FontAwesomeIcon icon={faPlusCircle} />
              </button>
              <label className="form-label">Conditions</label>
            </div>

            {editedToken?.Conditions?.map((cond, index) => (
              <div key={cond.Order} className="mb-3 token-condition">
                <div className="d-flex mb-2">
                  <div className="flex-grow-1">
                    <select
                      className="form-select"
                      value={cond?.Condition ?? ""}
                      onChange={(e) =>
                        setEditedToken((prev) => ({
                          ...prev,
                          Conditions: prev.Conditions.map((c, i) => {
                            if (i === index) {
                              return {
                                ...prev.Conditions[i],
                                Condition: e.target.value,
                              };
                            }
                            return c;
                          }),
                        }))
                      }
                    >
                      {editedToken?.Tag !== "counter" && (
                        <option value="one-of">one of</option>
                      )}
                      <option value="notempty">answered</option>
                      <option value="empty">not answered</option>
                      <option value="compare:lt">less than</option>
                      <option value="compare:lte">less than or equal to</option>
                      <option value="compare:eq">equal to</option>
                      <option value="compare:gt">greater than</option>
                      <option value="compare:gte">
                        greater than or equal to
                      </option>
                    </select>
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-sm border-0 btn-outline-danger float-end"
                      onClick={() =>
                        setEditedToken((prev) => ({
                          ...prev,
                          Conditions: prev.Conditions.filter(
                            (c, i) => i !== index
                          ),
                        }))
                      }
                    >
                      <FontAwesomeIcon icon={faMinusCircle} />
                    </button>
                  </div>
                </div>

                {cond?.Condition === "one-of" ? (
                  <div className="mb-2">
                    <Input
                      isMulti
                      isValidNewOption={(v) => v.trim().length > 0}
                      value={cond.ConditionText || []}
                      onChange={(values) =>
                        setEditedToken((prev) => ({
                          ...prev,
                          Conditions: prev.Conditions.map((c, i) => {
                            if (i === index) {
                              return {
                                ...prev.Conditions[i],
                                ConditionText: values,
                              };
                            }
                            return c;
                          }),
                        }))
                      }
                    />
                  </div>
                ) : (
                  cond?.Condition !== "empty" &&
                  cond?.Condition !== "notempty" && (
                    <div className="mb-2">
                      <input
                        className="form-control"
                        type="number"
                        value={cond?.ConditionNumber ?? ""}
                        onChange={(e) =>
                          setEditedToken((prev) => ({
                            ...prev,
                            Conditions: prev.Conditions.map((c, i) => {
                              if (i === index) {
                                return {
                                  ...prev.Conditions[i],
                                  ConditionNumber: parseInt(e.target.value, 10),
                                };
                              }
                              return c;
                            }),
                          }))
                        }
                      />
                    </div>
                  )
                )}

                <div className="mb-2">
                  <textarea
                    className="form-control"
                    value={cond?.Text}
                    onChange={(e) =>
                      setEditedToken((prev) => ({
                        ...prev,
                        Conditions: prev.Conditions.map((c, i) => {
                          if (i === index) {
                            return {
                              ...prev.Conditions[i],
                              Text: e.target.value,
                            };
                          }
                          return c;
                        }),
                      }))
                    }
                  />
                </div>
              </div>
            ))}

            <div className="mt-3 mb-5 alert alert-info">
              You can use [VALUE] to represent actual value in all text fields.
              If you are using conditions, then [CONDITION] can be used for the
              value matched in condition.
            </div>
          </>
        )
      }
    </>
  );
}

export default function TokenForm({
  token,
  onClose,
  tags = [
    "contact_field",
    "input_argument",
    "input_argument_count",
    "survey",
    "workflow_name",
    "guid",
    "component_name",
    "current_time",
    "customer_name",
  ],
  inputArgumentsFilter,
  inputArguments = [],
  currentTimeDefaultTimeZone,
}) {
  const dataCategories = useSelector(selectDataCategories);
  const participantCategories = useSelector(selectParticipantCategories);
  const counters = useSelector(selectCounterCategories);
  if (inputArgumentsFilter) {
    inputArguments = inputArguments.filter(inputArgumentsFilter);
  }
  const importedContactFields = useSelector(selectimportedContactFields);
  const eventHandlers = useSelector(selectEventHandlers);
  const [editedToken, setEditedToken] = useState(); // variable to be shown in the form

  const {
    data: contactFields,
    isFetching,
    isLoading,
  } = useGetContactFieldsQuery();

  const formQuestions = [];
  eventHandlers.forEach((eh) =>
    eh?.Rules?.forEach((r) => {
      r.Actions?.forEach((act) => {
        if (
          act.Type === "form-question" &&
          ((act.Options?.find((opt) => opt.Type === "Type")?.Value === "file" &&
            act.Options?.find((opt) => opt.Type === "FileType")?.Value ===
              "img") ||
            act.Options?.find((opt) => opt.Type === "Type")?.Value ===
              "signaturepad")
        ) {
          formQuestions.push(
            act.Options?.reduce(
              (prev, curr) => ({
                ...prev,
                [curr.Type]: curr.Value,
              }),
              { UUID: act.UUID }
            )
          );
        }
      });
    })
  );

  useEffect(() => {
    setEditedToken(tokenForEdit(token));
  }, [token]);

  const selectedVariable = inputArguments?.find(
    (arg) => arg.UUID === editedToken?.Value
  );

  const isVariableSystemDataStructure = [
    "contact",
    "asset",
    "event",
    "group",
    "ticket",
  ].includes(selectedVariable?.Format);

  const isVariableDataStructure =
    isVariableSystemDataStructure ||
    (["json", "data-entry"].includes(selectedVariable?.Format) &&
      selectedVariable?.DataStructure);

  const isGeoJSONVariable =
    editedToken?.Tag === "input_argument" &&
    selectedVariable?.Format === "geo-json";

  const geoJSONTokenFormat =
    editedToken?.Format && editedToken.Format !== "raw_value"
      ? editedToken.Format
      : "google_maps_link";

  const isAdvancedOptionsDisabled = !(
    isVariableDataStructure || selectedVariable?.IsMultiple
  );

  const { data: selectedDataStructure } = useGetApiDataCollectionQuery(
    selectedVariable?.DataStructure,
    {
      skip: !["json"].includes(selectedVariable?.Format),
    }
  );
  const { data: selectedDataEntryStructure } = useGetDataEntryStructureQuery(
    selectedVariable?.DataStructure,
    {
      skip: !["data-entry"].includes(selectedVariable?.Format),
    }
  );
  const { data: systemDataStructures } = useGetSystemDataStructuresQuery({
    skip: !isVariableSystemDataStructure,
  });

  const errors = {
    ...(editedToken?.Tag === "input_argument" &&
    ["date", "dateTime", "time"].includes(selectedVariable?.Format) &&
    !editedToken.DateFormatTo
      ? { DateFormatTo: "Date format is required for date/time variables" }
      : {}),
  };

  const handleClose = () => {
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix the errors before inserting the token.");
      return;
    }
    onClose(withTagPrinter(editedToken));
    setEditedToken(null);
  };

  const handleContextTokenSelect = (tag) => {
    if (!tag) {
      return;
    }

    const UUID = uuidv4();
    const tokenData = {
      UUID,
      Tag: tag,
      ...tokenCategoryOptions[tag],
      ...(tag === "current_time"
        ? {
            TimeZoneToValue: getDefaultCurrentTimeZone(
              currentTimeDefaultTimeZone
            ),
          }
        : {}),
    };

    onClose(withTagPrinter(tokenData));
  };

  const availableContextTokenOptions = contextTokenOptions.filter((option) =>
    tags.includes(option.tag)
  );

  const selectedDataField = [
    ...(["json"].includes(selectedVariable?.Format)
      ? selectedDataStructure?.Fields || []
      : []),
    ...(["data-entry"].includes(selectedVariable?.Format)
      ? selectedDataEntryStructure?.Fields || []
      : []),
    ...(["contact", "asset", "event", "group", "ticket"].includes(
      selectedVariable?.Format
    )
      ? systemDataStructures[selectedVariable?.Format]?.Fields || []
      : []),
  ].find((field) => field.Path === editedToken?.DataField);

  return (
    <>
      <>
        {tags.includes("reply-handler") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            value=""
            tabIndex={-1}
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "broadcast-handler",
                Value: e.target.value,
                DefaultValue: "",
                Label: e.target.value === "Reply is ",
                _value: e.target.options[e.target.selectedIndex].label,
              });
            }}
          >
            <option value="" hidden>
              INCOMING MESSAGE TOKENS
            </option>
            <option value="message">Incoming message content</option>
            <option value="original-title">Original message title</option>
            <option value="original-reference">
              Original message reference
            </option>
            <option value="original-description">
              Original message description
            </option>
          </select>
        )}

        {tags.includes("status-handler") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            value=""
            tabIndex={-1}
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "broadcast-handler",
                Value: e.target.value,
                DefaultValue: "",
                Label: e.target.value === "Status is ",
                _value: e.target.options[e.target.selectedIndex].label,
              });
            }}
          >
            <option value="" hidden>
              STATUS TOKEN
            </option>
            <option value="status-group">Status group</option>
            <option value="status-letter">Status letter</option>
          </select>
        )}

        {tags.includes("inbound-handler") && (
          <button
            type="button"
            className="btn btn-sm fw-bold bg-icon p-1"
            id="inputTokenInboundHandler"
            onClick={() => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "broadcast-handler",
                Value: "message",
                DefaultValue: "",
                Label: "Inbound message is",
                _value: "Inbound message",
              });
            }}
          >
            INBOUND MESSAGE
          </button>
        )}

        {tags.includes("contact_field") && contactFields && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            value=""
            tabIndex={-1}
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "contact_field",
                ...tokenCategoryOptions.contact_field,
                Value: e.target.value,
                _value: e.target.options[e.target.selectedIndex].label,
              });
            }}
          >
            <option value="" hidden>
              CONTACT FIELD
            </option>
            {contactFields
              .filter((cf) => !cf.isHidden)
              .map((cf) => (
                <option key={cf.uuid} value={cf.uuid}>
                  {cf.label}
                </option>
              ))}
          </select>
        )}

        {tags.includes("imported_contact_field") &&
          importedContactFields.length > 0 && (
            <select
              className="btn btn-sm fw-bold bg-icon p-1"
              value=""
              tabIndex={-1}
              onChange={(e) => {
                const UUID = uuidv4();
                onClose({
                  UUID,
                  Tag: "imported_contact_field",
                  ...tokenCategoryOptions.imported_contact_field,
                  Value: e.target.value,
                  _value: e.target.options[e.target.selectedIndex].label,
                });
              }}
            >
              <option value="" hidden>
                IMPORTED CONTACT FIELD
              </option>
              {importedContactFields.map((cf) => (
                <option key={cf} value={cf}>
                  {cf}
                </option>
              ))}
            </select>
          )}

        {tags.includes("input_argument") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="inputTokenInputArgument"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              const ia = inputArguments.find(
                (arg) => arg.UUID === e.target.value
              );
              onClose({
                UUID,
                Tag: "input_argument",
                ...tokenCategoryOptions.input_argument,
                Value: e.target.value,
                _value: e.target.selectedOptions[0].label,
                ...(ia?.Format === "dateTime"
                  ? {
                      DateFormatTo: ia?.DateTimeFormat || "YYYY-MM-DD HH:mm",
                    }
                  : {}),
                ...(ia?.Format === "date"
                  ? {
                      DateFormatTo: ia?.DateTimeFormat || "YYYY-MM-DD",
                    }
                  : {}),
                ...(ia?.Format === "time"
                  ? {
                      DateFormatTo: ia?.DateTimeFormat || "HH:mm",
                    }
                  : {}),
                ...(ia?.Format === "geo-json"
                  ? {
                      Format: "google_maps_link",
                    }
                  : {}),
              });
            }}
          >
            <option value="" hidden>
              VARIABLE
            </option>
            {inputArguments.length === 0 && (
              <option value="" disabled>
                No variables available
              </option>
            )}
            {inputArguments.map((arg) => (
              <option key={arg.UUID} value={arg.UUID}>
                {arg.Label}
              </option>
            ))}
          </select>
        )}

        {tags.includes("input_argument_count") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="inputTokenIACount"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "input_argument_count",
                ...tokenCategoryOptions.input_argument_count,
                Value: e.target.value,
                _value: e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              VARIABLE COUNT
            </option>
            {inputArguments.length === 0 && (
              <option value="" disabled>
                No variables available
              </option>
            )}
            {inputArguments.map((arg) => (
              <option key={arg.UUID} value={arg.UUID}>
                {arg.Label}
              </option>
            ))}
          </select>
        )}

        {availableContextTokenOptions.length > 0 && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="inputTokenContext"
            value=""
            onChange={(e) => handleContextTokenSelect(e.target.value)}
          >
            <option value="" hidden>
              CONTEXT
            </option>
            {availableContextTokenOptions.map((option) => (
              <option key={option.tag} value={option.tag}>
                {option.label}
              </option>
            ))}
          </select>
        )}

        {tags.includes("input_argument_exists") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="inputTokenIAExists"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "input_argument_exists",
                ...tokenCategoryOptions.input_argument_exists,
                Value: e.target.value,
                _value: e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              VARIABLE EXISTS
            </option>
            {inputArguments.length === 0 && (
              <option value="" disabled>
                No variables available
              </option>
            )}
            {inputArguments.map((arg) => (
              <option key={arg.UUID} value={arg.UUID}>
                {arg.Label}
              </option>
            ))}
          </select>
        )}

        {tags.includes("monitor") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="monitorTagSelect"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "monitor",
                ...tokenCategoryOptions.monitor,
                Value: e.target.value,
                _value: "Monitor: " + e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              MONITOR
            </option>
            <option value="uuid">UUID</option>
            <option value="label">Label</option>
            <option value="description">Description</option>
            <option value="reference">Reference</option>
            <option value="priority">Priority</option>
            <option value="status">Status</option>
            <option value="kind">Kind</option>
          </select>
        )}

        {tags.includes("api") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="apiTagSelect"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "api",
                ...tokenCategoryOptions.api,
                Value: e.target.value,
                _value: "API: " + e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              API
            </option>
            <option value="uuid">UUID</option>
            <option value="label">Label</option>
            <option value="description">Description</option>
            <option value="reference">Reference</option>
            <option value="url">URL</option>
          </select>
        )}

        {tags.includes("ping_status") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="pingStatusTagSelect"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "ping_status",
                ...tokenCategoryOptions.ping_status,
                Value: e.target.value,
                _value: "Ping Status: " + e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              Ping Status
            </option>
            <option value="status">Status</option>
            <option value="response_code">Response Code</option>
            <option value="response_body">Response Body</option>
            <option value="time_utc">Time UTC</option>
          </select>
        )}

        {tags.includes("ping_status_short") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="pingStatusTagSelect"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "ping_status",
                ...tokenCategoryOptions.ping_status,
                Value: e.target.value,
                _value: "Ping Status: " + e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              Ping Status
            </option>
            <option value="status">Status</option>
            <option value="response_code">Response Code</option>
            <option value="time_utc">Time UTC</option>
          </select>
        )}

        {tags.includes("weather") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="weatherTagSelect"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "weather",
                ...tokenCategoryOptions.weather,
                Value: e.target.value,
                _value: "Weather: " + e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              WEATHER
            </option>
            {/* long_message (not in subject and from),
              short_message (not in subject and from),
              regions (not in subject and from),
              counties (not in subject and from),
              alert_message (not in subject and from),
              alert_ref (ALLOW in subject and from) */}
            <option value="long_message">Long Message</option>
            <option value="short_message">Short Message</option>
            <option value="regions">Regions</option>
            <option value="counties">Counties</option>
            <option value="alert_message">Alert Message</option>
            <option value="alert_ref">Alert Reference</option>
          </select>
        )}

        {tags.includes("weather_short") && (
          <select
            className="btn btn-sm fw-bold bg-icon p-1"
            id="weatherTagSelect"
            value=""
            onChange={(e) => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "weather",
                ...tokenCategoryOptions.weather,
                Value: e.target.value,
                _value: "Weather: " + e.target.selectedOptions[0].label,
              });
            }}
          >
            <option value="" hidden>
              WEATHER
            </option>
            {/* long_message (not in subject and from),
              short_message (not in subject and from),
              regions (not in subject and from),
              counties (not in subject and from),
              alert_message (not in subject and from),
              alert_ref (ALLOW in subject and from) */}
            <option value="alert_ref">Alert Reference</option>
          </select>
        )}

        {tags.includes("assets") && (
          <button
            type="button"
            className="btn btn-sm fw-bold bg-icon px-2 py-0"
            id="inputTokenAsset"
            onClick={() => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "assets",
                ...tokenCategoryOptions.assets,
              });
            }}
          >
            ASSETS TABLE
          </button>
        )}

        {tags.includes("events") && (
          <button
            type="button"
            className="btn btn-sm fw-bold bg-icon px-2 py-0"
            id="inputTokenEvent"
            onClick={() => {
              const UUID = uuidv4();
              onClose({
                UUID,
                Tag: "events",
                ...tokenCategoryOptions.events,
              });
            }}
          >
            EVENTS TABLE
          </button>
        )}
      </>

      <Offcanvas
        show={editedToken}
        onHide={handleClose}
        placement="end"
        className="z-3000"
        backdropClassName="z-2999"
      >
        <Offcanvas.Header closeButton className="bg-colored-reverse text-white">
          <Offcanvas.Title className="fw-bold">Edit Token</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          {editedToken && (
            <div>
              <h3>{editedToken?._label}</h3>
              <div className="mb-3">
                {
                  // Contact field
                  editedToken?.Tag === "contact_field" && contactFields && (
                    <>
                      <label
                        htmlFor="inputTokenValue"
                        className="form-label text-capitalize"
                      >
                        Contact field
                      </label>
                      <select
                        className="form-select"
                        id="inputTokenValue"
                        value={editedToken?.Value ?? ""}
                        onChange={(e) =>
                          setEditedToken((prev) => ({
                            ...prev,
                            Value: e.target.value,
                            _value:
                              e.target.options[e.target.selectedIndex].label,
                          }))
                        }
                      >
                        <option value="" disabled>
                          Select a field
                        </option>
                        {contactFields
                          .filter((cf) => !cf.isHidden)
                          .map((cf) => (
                            <option key={cf.uuid} value={cf.uuid}>
                              {cf.label}
                            </option>
                          ))}
                      </select>
                    </>
                  )
                }

                {
                  // question select for image
                  editedToken?.Tag === "image" && (
                    <>
                      <label htmlFor="questionInput" className="form-label">
                        Question
                      </label>
                      <select
                        className="form-select"
                        id="questionInput"
                        value={editedToken?.Value ?? ""}
                        onChange={(e) =>
                          setEditedToken((prev) => ({
                            ...prev,
                            Value: e.target.value,
                            _value: e.target.selectedOptions[0].label,
                          }))
                        }
                      >
                        <option value="" disabled>
                          Select a question
                        </option>
                        {formQuestions.map((q) => (
                          <option key={q.UUID} value={q.UUID}>
                            {q.Title}
                          </option>
                        ))}
                      </select>
                    </>
                  )
                }

                {
                  // Variable
                  (editedToken?.Tag === "input_argument" ||
                    editedToken?.Tag === "input_argument_count" ||
                    editedToken?.Tag === "input_argument_exists") && (
                    <>
                      <label htmlFor="inputTokenValue" className="form-label">
                        Variable
                      </label>
                      <select
                        className="form-select"
                        id="inputTokenValue"
                        value={editedToken?.Value ?? ""}
                        onChange={(e) => {
                          const ia = inputArguments.find(
                            (arg) => arg.UUID === e.target.value
                          );
                          setEditedToken((prev) => ({
                            ...prev,
                            Value: e.target.value,
                            _value: e.target.selectedOptions[0].label,
                            ...(ia?.Format === "dateTime"
                              ? {
                                  DateFormatTo:
                                    ia?.DateTimeFormat || "YYYY-MM-DD HH:mm",
                                }
                              : {}),
                            ...(ia?.Format === "date"
                              ? {
                                  DateFormatTo:
                                    ia?.DateTimeFormat || "YYYY-MM-DD",
                                }
                              : {}),
                            ...(ia?.Format === "time"
                              ? {
                                  DateFormatTo: ia?.DateTimeFormat || "HH:mm",
                                }
                              : {}),
                            DataStructureTagPrinters: undefined,
                            DataField: undefined,
                          }));
                        }}
                      >
                        <option value="">Select a variable</option>
                        {inputArguments.map((arg) => (
                          <option key={arg.UUID} value={arg.UUID}>
                            {arg.Label}
                          </option>
                        ))}
                      </select>
                    </>
                  )
                }

                {!editedToken?.Value &&
                !tokensWithoutValue.includes(editedToken?.Tag) ? (
                  <span className="text-danger">
                    You need to make a selection
                  </span>
                ) : null}
              </div>

              {
                // Is main thread?
                ["input_argument", "input_argument_count"].includes(
                  editedToken?.Tag
                ) && (
                  <div className="mb-3 form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="tokenUseDefaultRuntimeBranch"
                      checked={editedToken?.UseMainThread ?? false}
                      onChange={() =>
                        setEditedToken((prev) => ({
                          ...prev,
                          UseMainThread: !prev.UseMainThread,
                        }))
                      }
                    />
                    <label
                      className="form-check-label"
                      htmlFor="tokenUseDefaultRuntimeBranch"
                    >
                      Use main thread?
                    </label>
                  </div>
                )
              }

              <StyledTabContainer>
                <Tab.Container
                  defaultActiveKey="options"
                  id="token-form-tabs"
                  className="mb-3"
                  key={editedToken?.Value}
                >
                  {![
                    "survey",
                    "input_argument_count",
                    "contact_field",
                    "monitor",
                    "api",
                    "ping_status",
                    "assets",
                    "events",
                    "weather",
                  ].includes(editedToken?.Tag) && (
                    <Nav variant="tabs">
                      <div className="flex-grow-1"></div>
                      <Nav.Item>
                        <Nav.Link eventKey="options">Format</Nav.Link>
                      </Nav.Item>
                      <Nav.Item>
                        <Nav.Link
                          disabled={isAdvancedOptionsDisabled}
                          eventKey="advanced"
                          className={`${
                            isAdvancedOptionsDisabled ? "text-muted" : ""
                          }`}
                        >
                          Advanced Options
                        </Nav.Link>
                      </Nav.Item>
                    </Nav>
                  )}
                  <Tab.Content>
                    <Tab.Pane eventKey="options" title="Options">
                      {![
                        "survey",
                        "input_argument_count",
                        "contact_field",
                        "monitor",
                        "api",
                        "ping_status",
                        "assets",
                        "events",
                        "weather",
                        ...simpleWorkflowRuntimeTags,
                      ].includes(editedToken?.Tag) && (
                        <div className="mb-3">
                          <Input
                            label="Print as"
                            inputType="radio"
                            radioName="tokenPrintAs"
                            value={
                              isGeoJSONVariable
                                ? [geoJSONTokenFormat]
                                : editedToken?.PrintAs
                                ? [editedToken?.PrintAs]
                                : ["csv"]
                            }
                            onChange={(values) =>
                              setEditedToken((prev) => ({
                                ...prev,
                                ...(isGeoJSONVariable
                                  ? {
                                      Format: values[0],
                                      PrintAs: "csv",
                                    }
                                  : { PrintAs: values[0] }),
                              }))
                            }
                            options={
                              isGeoJSONVariable
                                ? [
                                    {
                                      value: "google_maps_link",
                                      label: "Google Maps link",
                                    },
                                    {
                                      value: "raw_json",
                                      label: "Raw JSON",
                                    },
                                  ]
                                : [
                                    {
                                      value: "csv",
                                      label: "Plain text (CSV)",
                                    },
                                    {
                                      value: "table",
                                      label: "HTML Table",
                                    },
                                  ]
                            }
                          />
                        </div>
                      )}

                      {isVariableDataStructure &&
                        editedToken?.Tag !== "input_argument_count" && (
                          <>
                            {editedToken?.PrintAs !== "table" && (
                              <>
                                <div className="mb-3">
                                  <label
                                    htmlFor="inputTokenSeparator"
                                    className="form-label"
                                  >
                                    Separator for Records
                                    <HelpButton helpKey="message-token-separator" />
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="Separator"
                                    className="form-control"
                                    id="inputTokenSeparator"
                                    value={editedToken?.Separator ?? ""}
                                    onChange={(e) =>
                                      setEditedToken((prev) => ({
                                        ...prev,
                                        Separator: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                                <div className="mb-3">
                                  <label
                                    htmlFor="inputTokenSeparator"
                                    className="form-label"
                                  >
                                    Separator for Fields
                                    <HelpButton helpKey="message-token-separator" />
                                  </label>
                                  <input
                                    type="text"
                                    placeholder="FieldSeparator"
                                    className="form-control"
                                    id="inputTokenSeparator"
                                    value={
                                      editedToken?.FieldsSeparator ??
                                      editedToken?.FieldSeparator ??
                                      ""
                                    }
                                    onChange={(e) =>
                                      setEditedToken((prev) => ({
                                        ...prev,
                                        FieldSeparator: e.target.value,
                                        FieldsSeparator: e.target.value,
                                      }))
                                    }
                                  />
                                </div>
                              </>
                            )}

                            <div className="mb-3">
                              <label
                                htmlFor="inputTokenValueDataFields"
                                className="form-label"
                              >
                                Data fields
                              </label>
                              <div className="d-flex">
                                <select
                                  className="form-select"
                                  id="inputTokenValueDataFields"
                                  value={editedToken?.DataField ?? ""}
                                  onChange={(e) => {
                                    setEditedToken((prev) => ({
                                      ...prev,
                                      DataField: e.target.value,
                                    }));
                                  }}
                                >
                                  <option value="" disabled>
                                    Select a data field
                                  </option>
                                  {["json"].includes(
                                    selectedVariable?.Format
                                  ) &&
                                    selectedDataStructure?.Fields?.map(
                                      (arg) => (
                                        <option key={arg.Path} value={arg.Path}>
                                          {arg.Name} ( {arg.Path} ){" "}
                                          {!!editedToken
                                            ?.DataStructureTagPrinters?.[
                                            arg.Path
                                          ] && <span> &#10004; </span>}
                                        </option>
                                      )
                                    )}
                                  {["data-entry"].includes(
                                    selectedVariable?.Format
                                  ) &&
                                    selectedDataEntryStructure?.Fields?.map(
                                      (arg) => (
                                        <option key={arg.Path} value={arg.Path}>
                                          {arg.Name}{" "}
                                          {!!editedToken
                                            ?.DataStructureTagPrinters?.[
                                            arg.Path
                                          ] && <span> &#10004; </span>}
                                        </option>
                                      )
                                    )}
                                  {[
                                    "contact",
                                    "asset",
                                    "event",
                                    "group",
                                    "ticket",
                                  ].includes(selectedVariable?.Format) &&
                                    systemDataStructures[
                                      selectedVariable?.Format
                                    ]?.Fields?.map((arg) => (
                                      <option key={arg.Path} value={arg.Path}>
                                        {arg.Name}
                                        {!!editedToken
                                          ?.DataStructureTagPrinters?.[
                                          arg.Path
                                        ] && <span>&#10004;</span>}
                                      </option>
                                    ))}
                                </select>
                                {!!editedToken?.DataStructureTagPrinters?.[
                                  editedToken?.DataField
                                ] ? (
                                  <button
                                    className="btn btn-colored"
                                    onClick={(f) =>
                                      setEditedToken((prev) => {
                                        const newDataStructureTagPrinters =
                                          Object.entries(
                                            prev.DataStructureTagPrinters
                                          )
                                            .slice()
                                            .sort(
                                              (a, b) => a[1].Order - b[1].Order
                                            )
                                            .filter(
                                              ([key]) =>
                                                key !== editedToken?.DataField
                                            )
                                            .reduce(
                                              (acc, [key, value], Order) => {
                                                acc[key] = { ...value, Order };

                                                return acc;
                                              },
                                              {}
                                            );

                                        delete newDataStructureTagPrinters[
                                          editedToken?.DataField
                                        ];
                                        return {
                                          ...prev,
                                          DataStructureTagPrinters:
                                            newDataStructureTagPrinters,
                                        };
                                      })
                                    }
                                  >
                                    <FontAwesomeIcon icon={faTrash} />
                                  </button>
                                ) : (
                                  <button
                                    className="btn btn-colored"
                                    onClick={(f) =>
                                      setEditedToken((prev) => ({
                                        ...prev,
                                        DataStructureTagPrinters: {
                                          ...prev.DataStructureTagPrinters,
                                          [editedToken?.DataField]: {
                                            DataStructureField:
                                              editedToken?.DataField,
                                            Label:
                                              selectedDataField?.Name ?? "",
                                            DefaultValue: "[VALUE]",
                                            Order: Object.keys(
                                              prev.DataStructureTagPrinters ||
                                                {}
                                            ).length,
                                          },
                                        },
                                      }))
                                    }
                                    disabled={!editedToken?.DataField}
                                  >
                                    <FontAwesomeIcon icon={faPlus} />
                                  </button>
                                )}
                              </div>
                              {Object.entries(
                                editedToken?.DataStructureTagPrinters || {}
                              ).length === 0 && (
                                <div className="alert alert-danger p-1 mt-1">
                                  No data fields selected. Nothing to print.
                                </div>
                              )}
                            </div>
                          </>
                        )}

                      {!(
                        isVariableDataStructure &&
                        editedToken?.Tag !== "input_argument_count"
                      ) && (
                        <TokenCommonFields
                          editedToken={editedToken}
                          setEditedToken={setEditedToken}
                          selectedVariable={selectedVariable}
                          errors={errors}
                          inputArguments={inputArguments}
                          dataCategories={dataCategories}
                        />
                      )}

                      {(["json"].includes(selectedVariable?.Format) ||
                        selectedVariable?.DataStructure) &&
                        selectedDataStructure?.Fields?.filter(
                          (f) =>
                            f.Path === editedToken?.DataField &&
                            editedToken?.DataStructureTagPrinters?.[f.Path]
                        )?.map((arg) => (
                          <TokenCommonFields
                            fieldName={arg.Name}
                            editedToken={
                              editedToken.DataStructureTagPrinters?.[arg.Path]
                            }
                            setEditedToken={(f) =>
                              setEditedToken((prev) => ({
                                ...prev,
                                DataStructureTagPrinters: {
                                  ...prev.DataStructureTagPrinters,
                                  [arg.Path]: f(
                                    prev.DataStructureTagPrinters?.[arg.Path] ||
                                      {}
                                  ),
                                },
                              }))
                            }
                            selectedVariable={selectedVariable}
                            errors={errors}
                            inputArguments={inputArguments}
                            dataCategories={dataCategories}
                          />
                        ))}

                      {(["data-entry"].includes(selectedVariable?.Format) ||
                        selectedVariable?.DataStructure) &&
                        selectedDataEntryStructure?.Fields?.filter(
                          (f) =>
                            f.Path === editedToken?.DataField &&
                            editedToken?.DataStructureTagPrinters?.[f.Path]
                        )?.map((arg) => (
                          <TokenCommonFields
                            fieldName={arg.Name}
                            editedToken={
                              editedToken.DataStructureTagPrinters?.[arg.Path]
                            }
                            setEditedToken={(f) =>
                              setEditedToken((prev) => ({
                                ...prev,
                                DataStructureTagPrinters: {
                                  ...prev.DataStructureTagPrinters,
                                  [arg.Path]: f(
                                    prev.DataStructureTagPrinters?.[arg.Path] ||
                                      {}
                                  ),
                                },
                              }))
                            }
                            selectedVariable={selectedVariable}
                            errors={errors}
                            inputArguments={inputArguments}
                            dataCategories={dataCategories}
                          />
                        ))}

                      {isVariableSystemDataStructure &&
                        systemDataStructures[
                          selectedVariable?.Format
                        ]?.Fields?.filter(
                          (f) =>
                            f.Path === editedToken?.DataField &&
                            editedToken?.DataStructureTagPrinters?.[f.Path]
                        )?.map((arg) => (
                          <TokenCommonFields
                            fieldName={arg.Name}
                            editedToken={
                              editedToken.DataStructureTagPrinters?.[arg.Path]
                            }
                            setEditedToken={(f) =>
                              setEditedToken((prev) => ({
                                ...prev,
                                DataStructureTagPrinters: {
                                  ...prev.DataStructureTagPrinters,
                                  [arg.Path]: f(
                                    prev.DataStructureTagPrinters?.[arg.Path] ||
                                      {}
                                  ),
                                },
                              }))
                            }
                            selectedVariable={selectedVariable}
                            errors={errors}
                            inputArguments={inputArguments}
                            dataCategories={dataCategories}
                          />
                        ))}
                    </Tab.Pane>
                    <Tab.Pane eventKey="advanced" title="Advanced Options">
                      {selectedVariable?.IsMultiple && (
                        <div className="my-4">
                          <CompoundInput
                            optionType={"Limit"}
                            label="Limit (for multiple values)"
                            defaultValue={[""]}
                            condition={editedToken}
                            values={[
                              {
                                Value:
                                  editedToken?.LimitVariableUuid ||
                                  editedToken?.Limit,
                                ValueFrom: editedToken?.LimitVariableUuid
                                  ? "inputArgument"
                                  : "static",
                                MainThread: !!editedToken?.LimitUseMainThread,
                              },
                            ]}
                            inputArgumentFilter={(ia) => ia.Format === "number"}
                            onChange={(values) => {
                              if (
                                values[0].ValueFrom === "inputArgument" &&
                                !inputArguments?.some(
                                  (ia) => ia.Format === "number"
                                )
                              ) {
                                toast.error(
                                  "You don't have any variable with number format."
                                );
                                return;
                              }
                              setEditedToken((prev) => ({
                                ...prev,
                                Limit:
                                  values[0].ValueFrom === "static"
                                    ? values[0].Value || 100
                                    : null,
                                LimitVariableUuid:
                                  values[0].ValueFrom === "inputArgument"
                                    ? inputArguments?.find(
                                        (ia) => ia.Format === "number"
                                      )?.UUID
                                    : null,
                                LimitUseMainThread:
                                  values[0].ValueFrom === "inputArgument"
                                    ? values[0].MainThread
                                    : null,
                              }));
                            }}
                          />

                          <CompoundInput
                            optionType={"Offset"}
                            label="Page (for multiple values)"
                            defaultValue={[""]}
                            condition={editedToken}
                            values={[
                              {
                                Value:
                                  editedToken?.OffsetVariableUuid ||
                                  editedToken?.Offset,
                                ValueFrom: editedToken?.OffsetVariableUuid
                                  ? "inputArgument"
                                  : "static",
                                MainThread: !!editedToken?.OffsetUseMainThread,
                              },
                            ]}
                            inputArgumentFilter={(ia) => ia.Format === "number"}
                            onChange={(values) => {
                              if (
                                values[0].ValueFrom === "inputArgument" &&
                                !inputArguments?.some(
                                  (ia) => ia.Format === "number"
                                )
                              ) {
                                toast.error(
                                  "You don't have any variable with number format."
                                );
                                return;
                              }
                              setEditedToken((prev) => ({
                                ...prev,
                                Offset:
                                  values[0].ValueFrom === "static"
                                    ? values[0].Value || 0
                                    : null,
                                OffsetVariableUuid:
                                  values[0].ValueFrom === "inputArgument"
                                    ? inputArguments?.find(
                                        (ia) => ia.Format === "number"
                                      )?.UUID
                                    : null,
                                OffsetUseMainThread:
                                  values[0].ValueFrom === "inputArgument"
                                    ? values[0].MainThread
                                    : null,
                              }));
                            }}
                          />
                        </div>
                      )}
                      {isVariableDataStructure && (
                        <div className="my-4">
                          <h5>Field order</h5>
                          {Object.entries(
                            editedToken?.DataStructureTagPrinters || {}
                          )
                            .sort((a, b) => a[1].Order > b[1].Order)
                            .map(
                              ([key, f], i, sortedDataStructureTagPrinters) => {
                                const selectedField =
                                  selectedVariable?.Format === "json"
                                    ? (
                                        selectedDataStructure?.Fields || []
                                      ).find((field) => field.Path === key)
                                    : selectedVariable?.Format === "data-entry"
                                    ? (
                                        selectedDataEntryStructure?.Fields || []
                                      ).find((field) => field.Path === key)
                                    : selectedVariable?.Format === "contact"
                                    ? contactFields.find(
                                        (field) => field.uuid === key
                                      )
                                    : systemDataStructures[
                                        selectedVariable?.Format
                                      ]?.Fields.find(
                                        (field) => field.Path === key
                                      );
                                return (
                                  <div
                                    key={key}
                                    className="d-flex justify-content-between align-items-center mb-2"
                                  >
                                    <div>
                                      <strong title={key}>
                                        {selectedField?.Name ||
                                          selectedField?.label ||
                                          key}
                                      </strong>
                                    </div>
                                    <div className="">
                                      <button
                                        className="btn p-0 px-1 border-0 btn-outline-secondary"
                                        disabled={i === 0}
                                        onClick={() =>
                                          setEditedToken((prev) => {
                                            const newDataStructureTagPrinters =
                                              {};
                                            sortedDataStructureTagPrinters.forEach(
                                              ([k, v], ii) => {
                                                newDataStructureTagPrinters[k] =
                                                  {
                                                    ...v,
                                                    Order:
                                                      ii === i
                                                        ? i - 1
                                                        : ii === i - 1
                                                        ? i
                                                        : ii,
                                                  };
                                              }
                                            );

                                            return {
                                              ...prev,
                                              DataStructureTagPrinters:
                                                newDataStructureTagPrinters,
                                            };
                                          })
                                        }
                                      >
                                        &uarr;
                                      </button>
                                      <button
                                        className="btn p-0 px-1 border-0 btn-outline-secondary"
                                        disabled={
                                          i ===
                                          sortedDataStructureTagPrinters.length -
                                            1
                                        }
                                        onClick={() =>
                                          setEditedToken((prev) => {
                                            const newDataStructureTagPrinters =
                                              {};
                                            sortedDataStructureTagPrinters.forEach(
                                              ([k, v], ii) => {
                                                newDataStructureTagPrinters[k] =
                                                  {
                                                    ...v,
                                                    Order:
                                                      ii === i
                                                        ? i + 1
                                                        : ii === i + 1
                                                        ? i
                                                        : ii,
                                                  };
                                              }
                                            );

                                            return {
                                              ...prev,
                                              DataStructureTagPrinters:
                                                newDataStructureTagPrinters,
                                            };
                                          })
                                        }
                                      >
                                        &darr;
                                      </button>
                                    </div>
                                  </div>
                                );
                              }
                            )}
                        </div>
                      )}

                      {isVariableDataStructure && (
                        <>
                          <div className="my-4">
                            <label className="form-label">Default value</label>
                            <textarea
                              className="form-control"
                              value={editedToken?.DefaultValue}
                              onChange={(e) =>
                                setEditedToken((prev) => ({
                                  ...prev,
                                  DefaultValue: e.target.value,
                                }))
                              }
                            />
                          </div>
                        </>
                      )}
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </StyledTabContainer>
            </div>
          )}
          <JsonCollapse json={editedToken} />
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
